<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed } from 'vue';
import {
  avatarText,
  blockOwnerButtonTitle,
  canBlockOwnerPhone,
  cellValue,
  feedDetails as feedDetailsForColumns,
  formatDate,
  isMidasActionVisible,
  isOwnerPhoneColumn,
  isOwnerUnconfirmedField,
  isRefusalActionVisible,
  isRegisterActionVisible,
  itemStats,
  midasButtonTitle,
  ownerConfirmationTooltip,
  ownerFieldCardClass,
  ownerFieldTitle,
  propertySubtitle,
  propertyTitle,
  refusalButtonTitle,
  statusTone,
} from '../utils/pendentesFormatters';
import {
  hiddenSourceLinksCount,
  visibleSourceLinks,
} from '../utils/pendentesSourceLinks';
import { usePendentesRowActions } from '../composables/usePendentesRowActions';

const props = defineProps({
  item: { type: Object, required: true },
  columns: { type: Array, default: () => [] },
  updatingMidasItemId: { type: String, default: '' },
  registeringLaudoItemId: { type: String, default: '' },
  savingRefusalItemId: { type: String, default: '' },
});

const emit = defineEmits([
  'midas',
  'register',
  'refuse',
  'blockOwnerPhone',
  'openSourceLinks',
]);

const {
  isLaudoRegisteringItem,
  registerButtonTitle,
  isMidasButtonDisabled,
  isRegisterButtonDisabled,
  isRefusalButtonDisabled,
} = usePendentesRowActions({
  updatingMidasItemId: computed(() => props.updatingMidasItemId),
  registeringLaudoItemId: computed(() => props.registeringLaudoItemId),
  savingRefusalItemId: computed(() => props.savingRefusalItemId),
});

function feedDetails(item) {
  return feedDetailsForColumns(item, props.columns);
}
</script>

<template>
  <article
    class="group overflow-hidden rounded-lais border border-lais-border bg-white shadow-lais-card transition hover:border-lais-primary/40 hover:shadow-lais-pop"
  >
    <header
      class="flex items-start gap-3 border-b border-lais-border-soft bg-white px-4 pb-3 pt-4"
    >
      <div
        class="grid size-12 shrink-0 place-items-center rounded-full bg-lais-primary-50 text-[13px] font-bold text-lais-primary ring-4 ring-lais-bg-page"
      >
        {{ avatarText(item) }}
      </div>
      <div class="min-w-0 flex-1">
        <h3
          class="line-clamp-2 text-[15px] font-semibold leading-snug text-lais-text"
        >
          {{ propertyTitle(item) }}
        </h3>
        <p
          v-if="propertySubtitle(item)"
          class="mt-0.5 line-clamp-2 text-[12px] text-lais-text-muted"
        >
          {{ propertySubtitle(item) }}
        </p>
      </div>
      <span
        class="shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1"
        :class="statusTone(item.status)"
      >
        {{ item.statusLabel || item.status }}
      </span>
    </header>

    <div class="px-4 pb-4 pt-3">
      <div
        v-if="itemStats(item).length"
        class="flex flex-wrap gap-2 text-[12px] font-semibold text-lais-text-muted"
      >
        <span
          v-for="stat in itemStats(item)"
          :key="stat.key"
          class="inline-flex items-center gap-1 rounded-full bg-lais-bg-page px-2.5 py-1"
        >
          <span class="size-3.5" :class="stat.icon" aria-hidden="true" />
          {{ stat.value }} {{ stat.suffix }}
        </span>
      </div>

      <div
        class="mt-3 flex flex-col gap-2 rounded-lais-sm border border-lais-border-soft bg-lais-bg-page p-2.5 sm:flex-row sm:items-center sm:justify-between"
      >
        <span
          class="inline-flex items-center gap-1.5 text-[12px] font-semibold text-lais-text-muted"
        >
          <span class="i-lucide-clock-3 size-3.5 shrink-0" aria-hidden="true" />
          Processado em {{ formatDate(item.processedAt) }}
        </span>
        <div class="flex flex-wrap gap-2">
          <template v-for="link in visibleSourceLinks(item)" :key="link.key">
            <span
              v-if="link.inactive"
              v-tooltip.top="link.title"
              :title="link.title"
              class="inline-flex"
            >
              <button
                type="button"
                disabled
                class="inline-flex h-9 cursor-not-allowed items-center gap-2 rounded-lais border border-red-200 bg-red-50 px-3 text-[12px] font-semibold text-red-700 opacity-90"
                :aria-label="link.title"
              >
                <span class="size-3.5" :class="link.icon" aria-hidden="true" />
                Abrir
              </button>
            </span>
            <a
              v-else
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[12px] font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
              :title="link.title"
            >
              <span class="size-3.5" :class="link.icon" aria-hidden="true" />
              {{ link.label }}
            </a>
          </template>
          <button
            v-if="hiddenSourceLinksCount(item)"
            type="button"
            class="inline-flex h-9 items-center gap-1 rounded-lais border border-lais-border bg-white px-2.5 text-[12px] font-semibold text-lais-text-muted transition hover:border-lais-primary hover:bg-lais-primary-50 hover:text-lais-primary"
            :aria-label="`Mostrar mais ${hiddenSourceLinksCount(item)} anúncio(s)`"
            @click="emit('openSourceLinks', item)"
          >
            +{{ hiddenSourceLinksCount(item) }}
          </button>
          <a
            v-if="item.houseUrl"
            :href="item.houseUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[12px] font-semibold text-lais-primary transition hover:border-lais-primary hover:bg-lais-primary-50"
            title="Abrir imóvel na carteira interna"
          >
            <span class="i-lucide-home size-3.5" aria-hidden="true" />
            Carteira interna
          </a>
          <button
            v-if="isMidasActionVisible(item)"
            v-tooltip.top="midasButtonTitle(item)"
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-primary bg-lais-primary px-3 text-[12px] font-semibold text-white transition hover:bg-lais-primary-600 disabled:cursor-not-allowed disabled:border-lais-border disabled:bg-white disabled:text-lais-text-muted"
            :title="midasButtonTitle(item)"
            :aria-label="midasButtonTitle(item)"
            :disabled="isMidasButtonDisabled(item)"
            @click="emit('midas', item)"
          >
            <span
              class="size-3.5"
              :class="
                updatingMidasItemId === String(item.id)
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-refresh-cw'
              "
              aria-hidden="true"
            />
            Midas
          </button>
          <button
            v-if="isRegisterActionVisible(item)"
            v-tooltip.top="registerButtonTitle(item)"
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-woot-600 bg-woot-600 px-3 text-[12px] font-semibold text-white transition hover:bg-woot-700 disabled:cursor-not-allowed disabled:border-lais-border disabled:bg-white disabled:text-lais-text-muted"
            :title="registerButtonTitle(item)"
            :aria-label="registerButtonTitle(item)"
            :disabled="isRegisterButtonDisabled(item)"
            @click="emit('register', item)"
          >
            <span
              class="size-3.5"
              :class="
                isLaudoRegisteringItem(item)
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-file-plus-2'
              "
              aria-hidden="true"
            />
            Cadastrar no Midas
          </button>
          <button
            v-if="isRefusalActionVisible(item)"
            v-tooltip.top="refusalButtonTitle(item)"
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-lais border border-red-100 bg-red-50 px-3 text-[12px] font-semibold text-red-700 transition hover:border-red-500 hover:bg-red-600 hover:text-white disabled:cursor-not-allowed disabled:border-lais-border disabled:bg-white disabled:text-lais-text-muted"
            :title="refusalButtonTitle(item)"
            :aria-label="refusalButtonTitle(item)"
            :disabled="isRefusalButtonDisabled(item)"
            @click="emit('refuse', item)"
          >
            <span
              class="size-3.5"
              :class="
                savingRefusalItemId === String(item.id)
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-ban'
              "
              aria-hidden="true"
            />
            Registrar negativa
          </button>
        </div>
      </div>

      <dl class="mt-3 grid grid-cols-2 gap-2">
        <div
          v-for="column in feedDetails(item)"
          :key="column.key"
          v-tooltip.top="
            isOwnerUnconfirmedField(item, column)
              ? ownerConfirmationTooltip(item)
              : null
          "
          class="min-w-0 rounded-lais-sm border px-2.5 py-2"
          :class="ownerFieldCardClass(item, column)"
          :title="ownerFieldTitle(item, column)"
        >
          <dt class="truncate text-[11px] font-semibold text-lais-text-muted">
            {{ column.label }}
          </dt>
          <dd
            class="mt-0.5 truncate text-[13px] font-semibold text-lais-text"
            :class="
              isOwnerUnconfirmedField(item, column) ? 'text-blue-900' : ''
            "
          >
            <span
              v-if="isOwnerPhoneColumn(column) && canBlockOwnerPhone(item)"
              class="inline-flex max-w-full items-center gap-1.5"
            >
              <span class="truncate">{{ cellValue(item, column) }}</span>
              <button
                v-tooltip.top="blockOwnerButtonTitle(item)"
                type="button"
                class="inline-grid size-6 shrink-0 place-items-center rounded-full border border-red-100 bg-white text-red-700 transition hover:border-red-500 hover:bg-red-600 hover:text-white"
                :title="blockOwnerButtonTitle(item)"
                :aria-label="blockOwnerButtonTitle(item)"
                @click.stop="emit('blockOwnerPhone', item)"
              >
                <span class="i-lucide-ban size-3" aria-hidden="true" />
              </button>
            </span>
            <span v-else>{{ cellValue(item, column) }}</span>
          </dd>
        </div>
      </dl>
    </div>
  </article>
</template>
